/**
 * Represents a country with its associated postal code information
 * @interface Country
 * @property {string} code - The two-letter country code (e.g., 'US', 'CA')
 * @property {string} name - The full name of the country
 * @property {string} flag - The country's flag emoji
 * @property {string} postalCodeLabel - The localized term for postal code (e.g., 'ZIP code', 'Postal code')
 * @property {string} postalCodePattern - Regular expression pattern for validating postal codes
 * @property {string} postalCodeExample - Example of a valid postal code for the country
 * @property {string} phonePattern - Regular expression pattern for validating phone numbers
 * @property {string} phoneExample - Example of a valid phone number for the country
 */
export type Country = {
  code: string;
  name: string;
  flag: string;
  postalCodeLabel: string;
  postalCodePattern: string;
  postalCodeExample: string;
  phonePattern: string;
  phoneExample: string;
};

/**
 * Predefined list of supported countries with their postal code information
 * @constant
 */
export const countries: Country[] = [
  {
    code: "US",
    name: "United States",
    flag: "🇺🇸",
    postalCodeLabel: "ZIP code",
    postalCodePattern: "^\\d{5}$",
    postalCodeExample: "12345",
    phonePattern:
      "^(\\+?1[\\s.-]?)?\\(?([0-9]{3})\\)?[\\s.-]?[0-9]{3}[\\s.-]?[0-9]{4}$",
    phoneExample: "(555) 123-4567",
  },
  // {
  //   code: "AU",
  //   name: "Australia",
  //   flag: "🇦🇺",
  //   postalCodeLabel: "Postcode",
  //   postalCodePattern: "^\\d{4}$",
  //   postalCodeExample: "2000",
  //   phonePattern:
  //     "^\\+?61[\\s.-]?\\(?([0-9]{2})\\)?[\\s.-]?[0-9]{4}[\\s.-]?[0-9]{4}$",
  //   phoneExample: "02 1234 5678",
  // },
  // {
  //   code: "NZ",
  //   name: "New Zealand",
  //   flag: "🇳🇿",
  //   postalCodeLabel: "Postcode",
  //   postalCodePattern: "^\\d{4}$",
  //   postalCodeExample: "6011",
  //   phonePattern:
  //     "^\\+?64[\\s.-]?\\(?([0-9]{1,3})\\)?[\\s.-]?[0-9]{3,7}[\\s.-]?[0-9]{3,7}$",
  //   phoneExample: "03 123 4567",
  // },
  {
    code: "CA",
    name: "Canada",
    flag: "🇨🇦",
    postalCodeLabel: "Postal code",
    postalCodePattern: "^[A-Za-z]\\d[A-Za-z][ -]?\\d[A-Za-z]\\d$",
    postalCodeExample: "M5A 1A1",
    phonePattern:
      "^\\+?1[\\s.-]?\\(?([0-9]{3})\\)?[\\s.-]?[0-9]{3}[\\s.-]?[0-9]{4}$",
    phoneExample: "123 456 7890",
  },
];

/**
 * Mapping of locale codes to country codes for automatic country detection
 * @constant
 */
const localeToCountryMap: { [key: string]: string } = {
  "en-US": "US",
  "en-CA": "CA",
  "fr-CA": "CA",
  // Commented out till support is required
  // "en-AU": "AU",
  // "en-NZ": "NZ",
};

export function getUSCountry(): Country {
  return countries.find((c) => c.code === "US") || countries[0];
}

/**
 * Gets the appropriate country based on the user's browser locale
 * Falls back to US if no match is found
 *
 * @function
 * @returns {Country} The detected country object
 */
export function getCountryFromLocale(): Country {
  // Get browser language
  const browserLocale = navigator.language;

  // Try to match the full locale first
  let countryCode = localeToCountryMap[browserLocale];

  // If no match, try to match just the country part (last 2 characters)
  if (!countryCode && browserLocale.length >= 2) {
    countryCode = browserLocale.slice(-2).toUpperCase();
  }

  // Find the matching country or default to US
  const country = countries.find((c) => c.code === countryCode);
  return country || countries[0];
}

/**
 * Validates a postal code against a country's postal code pattern
 *
 * @function
 * @param {Country} country - The country to validate against
 * @param {string} postalCode - The postal code to validate
 * @returns {boolean} Whether the postal code is valid for the country
 */
export function validatePostalCode(
  country: Country,
  postalCode: string,
): boolean {
  const regex = new RegExp(country.postalCodePattern);
  return regex.test(postalCode);
}

/**
 * Formats a postal code according to country-specific rules
 * Currently supports special formatting for Canadian postal codes
 *
 * @function
 * @param {Country} country - The country whose formatting rules to apply
 * @param {string} postalCode - The postal code to format
 * @returns {string} The formatted postal code
 */
export function formatPostalCode(country: Country, postalCode: string): string {
  if (country.code === "CA") {
    // Format Canadian postal codes with a space in the middle
    const cleaned = postalCode.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (cleaned.length >= 6) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`;
    }
  }
  return postalCode;
}

/**
 * Formats a phone number for display and ensures E.164 format for backend
 * @param country The country to format for
 * @param phone The phone number to format
 * @returns The formatted phone number in E.164 format
 */
export function formatPhoneNumber(country: Country, phone: string): string {
  // Strip all non-digit characters
  const digitsOnly = phone.replace(/\D/g, "");

  // If empty, return as is
  if (digitsOnly.length === 0) return phone;

  // Format for E.164 (for backend)
  switch (country.code) {
    case "US":
      // For US, if it's 10 digits, add +1
      // If it's 11 digits and starts with 1, add +
      if (digitsOnly.length === 10) {
        return `+1${digitsOnly}`;
      } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
        return `+${digitsOnly}`;
      }
      break;
    case "AU":
      // For Australia, if it's 9 digits, add +61
      // If it's 11 digits and starts with 61, add +
      if (digitsOnly.length === 9) {
        return `+61${digitsOnly}`;
      } else if (digitsOnly.length === 11 && digitsOnly.startsWith("61")) {
        return `+${digitsOnly}`;
      }
      break;
    case "NZ":
      // For New Zealand, if it's 8-10 digits, add +64
      // If it's 10-12 digits and starts with 64, add +
      if (digitsOnly.length >= 8 && digitsOnly.length <= 10) {
        return `+64${digitsOnly}`;
      } else if (
        digitsOnly.length >= 10 &&
        digitsOnly.length <= 12 &&
        digitsOnly.startsWith("64")
      ) {
        return `+${digitsOnly}`;
      }
      break;
  }

  // If we couldn't format specifically, but it looks like a valid number,
  // add the country code as a fallback
  if (validatePhoneNumber(country, digitsOnly)) {
    switch (country.code) {
      case "US":
        return `+1${digitsOnly}`;
      case "AU":
        return `+61${digitsOnly}`;
      case "NZ":
        return `+64${digitsOnly}`;
      default:
        return `+${digitsOnly}`;
    }
  }

  // If all else fails, return the original input
  return phone;
}

/**
 * Validates a phone number for a specific country
 * @param country The country to validate for
 * @param phone The phone number to validate
 * @returns Whether the phone number is valid
 */
export function validatePhoneNumber(country: Country, phone: string): boolean {
  // Strip all non-digit characters
  const digitsOnly = phone.replace(/\D/g, "");

  // For US numbers, just check if it's a 10-digit number
  // or 11 digits starting with 1 (country code)
  if (country.code === "US") {
    return (
      digitsOnly.length === 10 ||
      (digitsOnly.length === 11 && digitsOnly.startsWith("1"))
    );
  }

  // For other countries, use their specific patterns
  // This is a simplified approach - the PhoneNumberInput component
  // uses google-libphonenumber for more robust validation and formatting
  switch (country.code) {
    case "AU":
      return (
        digitsOnly.length === 9 ||
        (digitsOnly.length === 11 && digitsOnly.startsWith("61"))
      );
    case "NZ":
      return (
        (digitsOnly.length >= 8 && digitsOnly.length <= 10) ||
        (digitsOnly.length >= 10 &&
          digitsOnly.length <= 12 &&
          digitsOnly.startsWith("64"))
      );
    default:
      // Fallback to basic validation - at least 10 digits
      return digitsOnly.length >= 10;
  }
}

/**
 * Formats a phone number for display to the user in a readable format
 * @param country The country to format for
 * @param phone The phone number to format
 * @returns The formatted phone number for display
 */
export function formatPhoneNumberForDisplay(
  country: Country,
  phone: string,
): string {
  // Strip all non-digit characters
  const digitsOnly = phone.replace(/\D/g, "");

  // If empty, return as is
  if (digitsOnly.length === 0) return phone;

  // Format for display based on country
  switch (country.code) {
    case "US":
      // Format as (XXX) XXX-XXXX for 10 digits
      if (digitsOnly.length === 10) {
        return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
      }
      // Format as +1 (XXX) XXX-XXXX for 11 digits starting with 1
      else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
        return `+1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7, 11)}`;
      }
      break;
    case "AU":
      // Format as XX XXXX XXXX for 9 digits
      if (digitsOnly.length === 9) {
        return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 6)} ${digitsOnly.slice(6, 9)}`;
      }
      // Format as +61 X XXXX XXXX for 11 digits starting with 61
      else if (digitsOnly.length === 11 && digitsOnly.startsWith("61")) {
        return `+61 ${digitsOnly.slice(2, 3)} ${digitsOnly.slice(3, 7)} ${digitsOnly.slice(7, 11)}`;
      }
      break;
    case "NZ":
      // Format based on length for NZ numbers
      if (digitsOnly.length >= 8 && digitsOnly.length <= 10) {
        const areaCode = digitsOnly.slice(0, 2);
        const middle = digitsOnly.slice(2, 5);
        const end = digitsOnly.slice(5);
        return `${areaCode} ${middle} ${end}`;
      }
      break;
  }

  // If we couldn't format specifically, return the original input
  return phone;
}

/**
 * Export the predefined countries list for external use
 */
export const defaultCountries = countries;
